export const GRID_WIDTH = 20;
export const GRID_HEIGHT = 14;
export const CELL_SIZE = 48;

export const PERFORMANCE = {
  TARGET_FPS: 60,
  FRAME_BUDGET_MS: 16.6,
  MAX_FRAME_TIME_MS: 16,
  AUTO_DEGRADE_THRESHOLD_FRAMES: 3,
  PATH_CACHE_SIZE: 32,
  CAN_PLACE_BUDGET_MS: 0.5,
  FIND_PATH_BUDGET_MS: 5,

  // ── BL-44 PerfGovernor ──────────────────────────────────────────────────
  // Generalises the old WebGL2Effects degrade window into ONE load brain WITH
  // recovery. The thresholds are deliberately ASYMMETRIC — degrade at the 16ms
  // budget, recover only when comfortably under 12ms — and the cooldowns are
  // asymmetric too (recover slower than degrade), so the ladder can never
  // oscillate on a borderline frame. (Fixes the old down-only / never-recovers
  // bug.) MAX_FRAME_TIME_MS + AUTO_DEGRADE_THRESHOLD_FRAMES above are reused as
  // the UP trigger; these add the window size + the DOWN (recovery) side.
  /** Frames in the reactive sliding sample buffer (was WebGL2Effects size 5). */
  GOVERNOR_FRAME_WINDOW: 5,
  /** Min gap between two UP steps (was WebGL2Effects.DEGRADE_COOLDOWN_MS). */
  DEGRADE_COOLDOWN_MS: 3000,
  /** avg frame-ms under this (across a full clean buffer) → step DOWN (recover). */
  RECOVER_FRAME_TIME_MS: 12,
  /** Comfortably-under frames in the buffer required to recover one rung. */
  RECOVER_THRESHOLD_FRAMES: 5,
  /** Recovery is slower than degrade so the ladder never flaps. */
  RECOVER_COOLDOWN_MS: 5000,
  /** Ladder depth: hit-stop → particles → density → chroma → bloom → motion. */
  GOVERNOR_MAX_LEVEL: 6,
} as const;

/**
 * Resolution cap for the WebGL2 post-processing layer (bloom + chromatic
 * aberration), expressed as a multiple of the *logical* game resolution
 * (GRID_WIDTH*CELL_SIZE × GRID_HEIGHT*CELL_SIZE = 960×672).
 *
 * The post-FX layer is a soft, low-frequency overlay composited over the crisp
 * 2D scene — it does NOT need full HiDPI resolution. On a DPR=2 display the
 * backing store is 1920×1344, so running the bloom/composite/chroma passes at
 * native resolution pays a 4× fill cost for an effect the eye cannot resolve at
 * that density. Capping the post-FX working resolution to logical pixels and
 * upscaling the result on composite (bilinear) is visually indistinguishable
 * while cutting the dominant render cost (renderWebglMs) by ~1/cap² on HiDPI.
 *
 * 1.0 = post-FX runs at logical resolution (recommended; no downscale on DPR=1
 * displays since the cap never exceeds the actual backing store). Raise toward
 * the display DPR for sharper bloom at higher cost; lower for more headroom.
 */
export const POST_FX_RESOLUTION_CAP = 1.0;

export const INPUT = {
  LONG_PRESS_MS: 500,
  MIN_TOUCH_TARGET_PX: 44,
} as const;

export const GAME = {
  // CR-3 (lever A): make leaks matter — a far tighter integrity buffer so the
  // player must keep watching from ~wave 15 (was 60, which let dozens leak with
  // zero consequence). Owner-signed starting value; tune live.
  INITIAL_HP: 25,
  INITIAL_CREDITS: 200,
  MAX_SKILL_SLOTS: 5,
  WAVES_ACT_I: 12,
  WAVES_ACT_II: 24,
  WAVES_ACT_III: 35, // CR-21→P23-6: compressed from 45
} as const;

export const FINAL_WAVE = GAME.WAVES_ACT_III;

/** BL-253: Echo Mode (endless, wave > FINAL_WAVE) rotates the authored
 *  late-game templates from this wave up to (not including) FINAL_WAVE —
 *  the FINAL boss wave (AXIOM-77 / `final_boss`) is excluded so the count
 *  scaling never multiplies AXIOM-77. See WaveManager.getEndlessTemplate.
 *
 *  ⚠️ CORRECTED (Spec B checkpoint, S-8): this guard does NOT do what an
 *  earlier reading of this comment claimed — it filters `final_boss` only,
 *  not the ordinary `boss` (Core Breaker) group. `boss` rotates into Echo
 *  Mode unguarded via the W30 template and was measured multiplying to 11 at
 *  W36, 13 at W41 before S-8. The actual boss-count guard (covering both the
 *  authored arc and Echo Mode) is `scaling.bossLivesDamageCapFraction` in
 *  `waves.json`, applied in `WaveManager.buildSpawnQueue` — see the S-8
 *  comment there. Do not copy this filter as a model for that guard. */
export const ENDLESS = {
  TEMPLATE_POOL_FIRST_WAVE: 30,
} as const;

/** Combat interaction radii, in pixels. Squared at the call site for
 *  distance comparison. Single source of truth (F-20). */
export const COMBAT = {
  PIERCE_RANGE: 80, // pierce hop-to-next-target radius
  CHAIN_RANGE: 80, // water chain bounce radius
  CLUSTER_RANGE: 96, // same-element cluster penalty radius (2 cells)
  FIREWALL_SLOW_RANGE: 72, // firewall proximity-slow radius (1.5 cells)
  SNIPER_PIERCE_HALF_WIDTH: 18, // level 4+ kinetic ray corridor half-width
} as const;

/* ── BL-174 / D-263 (S166) — the per-element CAPABILITY level gate ──────────────
 * Leveling a tower is now a DECISION, not just bigger numbers: fire/water/
 * lightning each unlock a real combat capability — fire's Scorch splash, water's
 * Cryo-Chain slow, lightning's Overload surge-crit — at this shared LEVEL GATE
 * (the kinetic L3/L4/L5 capability model, SniperCombat.ts:75,268,291). Gated LATE
 * on purpose: the early golden masters run L1 towers (W1-5 baseline, W1-3 grid-
 * draft) so they stay byte-identical; only the L3+ late fixtures re-baseline. The
 * magnitudes live per-tower in `towers.json` `special` (like every other special's
 * tuning); only the gate level is centralized here, leaving the machinery uniform
 * for the GOLD pinnacle tier (BL-175) to layer a higher gate on top. */
export const TOWER_CAPABILITY = {
  /** The level at which the bare-three (fire/water/lightning) capabilities unlock. */
  UNLOCK_LEVEL: 3,
} as const;

/* ── BL-175 / D-266 (S170) — the GOLD pinnacle tier (L6) ─────────────────────────
 * GOLD is the L6 crown that sits ABOVE the full L1→L5 arc — reachable only on a
 * fully-built (L5) tower, at a VERY steep price (≈the tower's entire prior
 * investment again; see Tower.getUpgradeCost). It grants each element its OWN
 * brand-new SIGNATURE mechanic (NOT an amplification of the L1–L5 arc): fire
 * HELIOS combustion-on-kill · water ABSOLUTE-ZERO cryo-nova · lightning TEMPEST
 * sky-strikes · kinetic ANNIHILATOR execute · signal ORACLE command-aura · virus
 * PANDEMIC death-propagation. Every mechanic is gated on THIS level so it has NO
 * surface in any golden master (the harness never drives the upgrade/`level=6`
 * path — masters max at L5 with direct-set stats) → all five masters stay
 * byte-identical (D-263/D-265 property, VERIFIED by re-running the suite, no
 * regen). The magnitudes live per-tower in `towers.json` `special` (the `gold*`
 * fields), like every other special's tuning; only the gate level + price knob
 * live here. NO count cap (owner steer "kein Cap — nur der Preis regelt") — the
 * price alone enforces scarcity. */
export const GOLD_TIER = {
  /** The pinnacle level. A tower at L5 (the prior `maxLevel`) can take ONE more
   *  upgrade to reach GOLD; this is the new `maxLevel`. */
  LEVEL: 6,
  /** The GOLD step costs this fraction of the tower's TOTAL prior investment
   *  (cost + every L1→L5 upgrade ≈ 8× base cost), i.e. ~the whole investment
   *  again × 1.5 → ~5× a normal late upgrade, draining the 1500→3500 surplus
   *  buffer (EconomySystem). Self-scaling per tower (an expensive kinetic GOLDs
   *  for ~2760, a cheap water for ~900) so the steep towers stay "kein
   *  Massenprodukt" without a hard cap. */
  COST_INVESTMENT_MULTIPLE: 1.5,
} as const;

/* ── Shield system (wave-15+ shielded enemies, S60) ─────────────── */

/** First wave at which non-boss enemies can spawn with a cyber-shield.
 *  D-322 / AC-5 — the byte-safe fallback; `waves.json` `shield.startWave`
 *  overrides via {@link applyShieldData} at boot (the FIREWALL_DEFAULTS
 *  mutable-singleton seam — every consumer imports this live binding, so no
 *  call site needs to change). */
export let SHIELD_START_WAVE = 15;

export interface ShieldTuning {
  /** Minimum shield spawn chance at SHIELD_START_WAVE. */
  CHANCE_MIN: number;
  /** Maximum shield spawn chance at FINAL_WAVE (clamped in endless). */
  CHANCE_MAX: number;
  /** Minimum shield HP as a fraction of the enemy's scaled maxHp. */
  HP_FRAC_MIN: number;
  /** Maximum shield HP fraction at FINAL_WAVE (clamped in endless). */
  HP_FRAC_MAX: number;
  /** EMP (lightning) damage multiplier against the shield pool. */
  EMP_MULT: number;
}

/** Shield tuning block — one-file balance for the late-game shield curve.
 *  D-322 / AC-5 — byte-safe fallback; overridden in place (fields mutated,
 *  not reassigned) by {@link applyShieldData} when `waves.json` `shield`
 *  ships a value, so every existing importer of this binding stays wired. */
export const SHIELD: ShieldTuning = {
  CHANCE_MIN: 0.25,
  CHANCE_MAX: 0.8,
  HP_FRAC_MIN: 0.4,
  HP_FRAC_MAX: 0.75,
  EMP_MULT: 2,
};

/** D-322 / AC-5 — apply the optional `waves.json` `shield` override onto
 *  {@link SHIELD} / {@link SHIELD_START_WAVE} in place. Absent/unset fields
 *  keep the byte-identical code default. Mirrors `applyFirewallData`
 *  (`entities/Firewall.ts`) — the established mutable-singleton override
 *  seam for a global tuning block with many existing readers. */
export function applyShieldData(
  override?: Partial<ShieldTuning & { startWave: number }> | null,
): void {
  if (!override) return;
  if (override.startWave !== undefined) SHIELD_START_WAVE = override.startWave;
  if (override.CHANCE_MIN !== undefined) SHIELD.CHANCE_MIN = override.CHANCE_MIN;
  if (override.CHANCE_MAX !== undefined) SHIELD.CHANCE_MAX = override.CHANCE_MAX;
  if (override.HP_FRAC_MIN !== undefined)
    SHIELD.HP_FRAC_MIN = override.HP_FRAC_MIN;
  if (override.HP_FRAC_MAX !== undefined)
    SHIELD.HP_FRAC_MAX = override.HP_FRAC_MAX;
  if (override.EMP_MULT !== undefined) SHIELD.EMP_MULT = override.EMP_MULT;
}

/* ── BL-152 (S154) — the two per-act "new threats" (Jammer · Overseer) ─────── */

/** S154 — the Act II JAMMER's tower-suppression. While a Jammer lives, every
 *  tower within its `special.jamRadius` runs on "slow-time": its per-frame dt is
 *  scaled by FIRE_RATE_MULT, throttling fire-rate / beam-DPS / sniper-charge
 *  across EVERY tower type at ONE centralized lever (`CombatSystem.updateTowers`).
 *  The stamp is refreshed each tick the tower is in range and decays over
 *  WINDOW_MS once the Jammer dies / leaves, so killing it (or spreading towers
 *  out of one field) restores fire-rate. Deterministic — no `enemyRng` draw.
 *  AUTHORITY (BL-164, S160 audit): the SHIPPING strength is **data-driven** —
 *  `jammer.special.fireRateMult` (0.4) + `.windowMs` (130) in `enemies.json`
 *  override these; the values below are the engine-side fallback defaults only. */
export const JAM = {
  /** Fallback tower-time multiplier (data: 0.4). 0.5 ⇒ ~half fire-rate/DPS. */
  FIRE_RATE_MULT: 0.5,
  /** Fallback jam-stamp lifetime without a refresh (ms; data: 130). Once the
   *  Jammer dies the tower un-jams in ~WINDOW_MS/dt frames (a snappy recovery). */
  WINDOW_MS: 120,
} as const;

/** S154 — the Act III OVERSEER's force-multiplier aura. While the host lives,
 *  every NON-overseer enemy within `special.auraRadius` takes DAMAGE_TAKEN_MULT ×
 *  incoming damage (a bounded mitigation — binary, never stacks). The buff is
 *  refreshed each tick and decays over WINDOW_MS, so focus-firing the host
 *  collapses the aura (the named counter-play). Deterministic — no `enemyRng`.
 *  AUTHORITY (BL-164, S160 audit): the SHIPPING strength is **data-driven** —
 *  `overseer.special.damageTakenMult` (0.5) + `.windowMs` (160) in `enemies.json`
 *  override these; the values below are the engine-side fallback defaults only. */
export const AURA = {
  /** Fallback incoming-damage multiplier on a buffed enemy (data: 0.5). */
  DAMAGE_TAKEN_MULT: 0.6,
  /** Fallback buff lifetime without a refresh (ms; data: 160) — collapse grace. */
  WINDOW_MS: 150,
} as const;

/** XOR salt for deriving the shield-specific RNG sub-stream from the main seed. */
export const SHIELD_SEED_SALT = 0x5e1d_0060;

/** XOR salt for deriving the spawn-layout RNG sub-stream from the run seed. */
export const SPAWN_SEED_SALT = 0x5e1d_0074;

/** XOR salt for the per-run BLOCKER re-roll (2026-08-11, owner request: a
 *  restart must produce a genuinely new board, "neues seed… neues grid").
 *
 *  Isolated for the same reason the spawn salt is: `createGrid`'s FIRST blocker
 *  placement draws from the SHARED main stream and that pre-advance is
 *  load-bearing for the full-run golden master, which drives `createGrid`
 *  directly (`tests/integration/sim-fullrun-baseline.test.ts`). The re-roll
 *  therefore runs off its own stream and only from `startRun`, which no golden
 *  master calls — so a restart varies the board without moving any fixture. */
export const BLOCKER_SEED_SALT = 0x5e1d_0088;

/** S141 / D-236 — XOR salt for the wave-mutation RNG sub-stream. Isolated from
 *  the combat/draft `gs.rng` (like the shield stream) so the mutation roll never
 *  perturbs crit/draft determinism — only the APPLIED stats move fixtures. */
export const MODIFIER_SEED_SALT = 0x5e1d_0088;

/** XOR salt for deriving the breach-channel RNG sub-stream from the run seed.
 *  BL-148 / D-247 — the dynamic breach channel's only random draw (the
 *  `BreachSystem.shouldBossBreach` roll) historically rode the SHARED combat
 *  stream (S149 §3), the lone spawn-adjacent draw that wasn't isolated like
 *  SPAWN/SHIELD/MODIFIER. This salt gives it its own stream so the roll never
 *  perturbs combat/draft/crit ordering. (Wired in P3; the value follows the
 *  `0x5e1d_00xx` family.) */
export const BREACH_SEED_SALT = 0x5e1d_009c;

/** BL-151 / D-250 — XOR salt for the enemy-behavior RNG sub-stream. The 5th
 *  isolated spawn/enemy stream (after SPAWN/SHIELD/MODIFIER/BREACH), following
 *  the same `0x5e1d_00xx` family (+0x14 step). Wired in `createSystems` and
 *  exposed on the `ENEMY_BEHAVIORS` context, but UNCONSUMED this session — the
 *  3 current specials are deterministic, so the stream never advances and every
 *  fixture stays byte-identical. It is the seam BL-152's rolled behaviours
 *  (Jammer / Aura-Host) will draw from without perturbing combat/draft/crit. */
export const ENEMY_SEED_SALT = 0x5e1d_00b0;

/**
 * BL-145 / D-247 — the data-driven spawn placement policy.
 *
 * S149 §2.4 found the spawn placement rules are 100 % in code (the fair zone,
 * the count of 4, the 2∶2 dormant∶hidden split, the `[8,16,24,30]` ladder, the
 * separation/overlap rails). This is the typed seam that lifts them into ONE
 * source — `DEFAULT_SPAWN_POLICY` is byte-identical to the old literals, and an
 * optional `DataLoader` override (the `spawn` key) can replace any field. No
 * populated override ships this session (public/data byte-identical); BL-146
 * lands the per-act geography on this seam.
 */
export interface SpawnPolicy {
  /** Total spawn cells chosen at grid-build (S149 floor: 4). */
  count: number;
  /** How many of `count` are dormant (visible); the rest are hidden. */
  dormantCount: number;
  /** Fair-flank zone: minimum eligible column on the top/bottom walls. */
  fairZoneColMin: number;
  /** Fair-flank zone: maximum eligible column on the top/bottom walls. */
  fairZoneColMax: number;
  /** Min A* breach-path length for a candidate to count as a real flank. */
  minBreachPathLength: number;
  /** Min Chebyshev separation enforced between any two chosen spawns (Bug 9). */
  minSeparation: number;
  /** Drop a candidate whose breach route overlaps primary above this (BL-137). */
  maxBreachPrimaryOverlap: number;
  /** Planned breach-eruption ladder across the run (CR-2). */
  scriptedBreachWaves: readonly number[];
  /** Max simultaneously-erupted secondary spawns (CR-2). */
  maxActiveSpawns: number;
  /** BL-146 / D-248 — the Act III "pincer" waves: on each, TWO (see
   *  {@link pincerCount}) spawns erupt together from OPPOSITE walls, converging
   *  on the core (the STORY_BIBLE "she breaches the walls" beat). Empty ⇒ no
   *  pincer (the pre-BL-146 single-eruption ladder). */
  pincerWaves: readonly number[];
  /** BL-146 / D-248 — how many spawns the pincer erupts concurrently (2). */
  pincerCount: number;
  /** BL-146 / D-248 — spawns the ORDINARY triggers (scripted/adaptive/boss)
   *  hold back so the Act III pincer always has material to erupt ≥2 from
   *  different walls, no matter how the Act I/II eruptions fell. The reserve is
   *  released once the pincer fires (post-pincer triggers see the full cap). */
  pincerReserve: number;
  /** AC-10 / OQ-3 (Spec B, O-5 branch (c)) — per-run jitter, in waves, applied
   *  to each entry of {@link scriptedBreachWaves} and {@link pincerWaves}.
   *
   *  O-5's Q-d verdict was "same waves every run, predictable": the ladder is a
   *  fixed constant, so the flank always opened at W15 and W21 and the pincer
   *  always at W26, on every run of every seed. A jitter of `n` moves each
   *  scripted wave by a seeded offset in `[-n, +n]`.
   *
   *  Drawn from the run's EXISTING isolated breach stream (`BREACH_SEED_SALT`),
   *  so the ladder stays exactly seed-reproducible — no new RNG stream and no
   *  new determinism surface (the pre-agreed OQ-3 constraint; six golden
   *  masters depend on it). Jittered waves are clamped so a breach can never
   *  land in Act I, preserving BL-146's "Act I is one clean lane".
   *
   *  0 (the default) reproduces the fixed ladder exactly. */
  scriptedBreachJitter?: number;
  /** AC-10 / OQ-3 (Spec B, O-5 branch (c)) — how many of the deepest eligible
   *  spawn cells the eruption picks among, seeded.
   *
   *  `pickNextSpawn` took NO RNG draw: among the wall-eligible pool it always
   *  took the deepest (largest col) cell, so WHERE a breach erupted was fully
   *  determined by the static layout — the other half of Q-d's "predictable".
   *  A value of `n > 1` picks seeded-uniformly among the `n` deepest eligible
   *  cells instead of always the single deepest.
   *
   *  The pick stays inside the ALREADY-ELIGIBLE pool, so every fairness
   *  guarantee is untouched: the opposite-wall preference (the converging
   *  pincer) still filters first, the cap/reserve gating is unchanged, and the
   *  chosen cell is still telegraphed and cinematically revealed by the caller
   *  before it emits — reveal-before-emit, the 2.5 s telegraph, and the
   *  no-unrevealed-eruption-adjacent-to-the-core guarantee all hold.
   *
   *  0 or 1 (the default) reproduces the deterministic deepest-cell pick. */
  spawnPickSeededDepth?: number;
}

/** The typed default — the shipping spawn policy.
 *
 *  The STATIC grid-build fields (`count`/`dormantCount`/`fairZone*`/
 *  `minBreachPathLength`/`minSeparation`/`maxBreachPrimaryOverlap`) remain
 *  byte-identical to the pre-BL-145 literals — so `createGrid` produces the
 *  SAME board and every real-grid golden master (`sim-fullrun-baseline`,
 *  `grid-draft-timewarp`, the `spawn-distribution` sweep) stays byte-identical.
 *
 *  BL-146 / D-248 — only the RUNTIME choreography fields carry the act-scaled
 *  geography (the determinism-cheapest lever, S149 §6.5): the scripted ladder
 *  now opens its first flank in Act II (`[15, 21]`, not the old Act-I `[8,…]`)
 *  so Act I (W1–12) is one clean lane, and the Act III pincer (`pincerWaves:
 *  [26]`) erupts ≥2 converging breaches from different walls. Because only the
 *  BreachSystem reads these (wired solely in the breach-witness harness +
 *  production), the lone fixture that moves is `sim-fullrun-breach-witness`. */
export const DEFAULT_SPAWN_POLICY: SpawnPolicy = {
  count: 4,
  dormantCount: 2,
  fairZoneColMin: 1,
  fairZoneColMax: 10,
  minBreachPathLength: 12,
  minSeparation: 4,
  maxBreachPrimaryOverlap: 0.5,
  scriptedBreachWaves: [15, 21],
  maxActiveSpawns: 4,
  pincerWaves: [26],
  pincerCount: 2,
  pincerReserve: 2,
  // AC-10 / OQ-3 (Spec B, O-5 branch (c)) — the answer to O-5's Q-d, "same
  // waves every run, predictable". Before this the ladder was a fixed constant
  // and `pickNextSpawn` took no draw, so the flank opened at W15/W21 and the
  // pincer at W26 on every run of every seed, always on the deepest cell. The
  // jitter moves WHEN a breach lands (clamped inside each wave's own act, so
  // the Act I clean / Act II flank / Act III pincer choreography is preserved)
  // and the depth pick moves WHERE, among the already-eligible cells.
  //
  // Both draw from the run's existing isolated breach stream, so a seed still
  // reproduces its run exactly and no new determinism surface is added.
  //
  // NOTE, corrected: an earlier revision of this comment blamed the witness's
  // "four spawns erupted before the pincer" on `effectiveActiveCap`'s
  // pincerReserve accounting not being ladder-aware. That was a hypothesis
  // recorded as if it were a cause, and it was WRONG. Measured: on the witness
  // seed the jittered ladder is scripted [15, 18], pincer [25]. The test
  // counted "pre-pincer" as `wave < 26`, so the pincer's own two eruptions at
  // W25 fell inside its own window (1 + 1 + 2 = the observed 4). The reserve
  // works correctly; the assertion was keyed to the authored constants that
  // the jitter deliberately moves. Fixed by having the witness read the run's
  // actual ladder (`BreachSystem.ladder`) instead of the literals.
  scriptedBreachJitter: 3,
  spawnPickSeededDepth: 3,
};

/** Merge an optional (data-driven) override onto {@link DEFAULT_SPAWN_POLICY}.
 *  An absent/empty override → the typed default unchanged (the byte-identical
 *  fallback). A partial override replaces only the named fields. */
export function resolveSpawnPolicy(
  override?: Partial<SpawnPolicy> | null,
): SpawnPolicy {
  if (!override) return DEFAULT_SPAWN_POLICY;
  return { ...DEFAULT_SPAWN_POLICY, ...override };
}

/** S141 / D-236 — Act II onward: the first wave a mutation can roll.
 *  BL-137 / D-242 (S145): pulled from W13 → W5 so the early-mid game is
 *  no longer AFK-trivial. The rate ramp in `WaveManager.mutationRate` puts
 *  W5–10 at the gentlest tier (~0.40) and Act III at 1.00 — every wave from
 *  W5 now asks a question (a mutation banner + the surviving-HP it implies). */
export const MUTATION_START_WAVE = 5;
